import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/playground.default.js';
import * as wireframeSkin from './skins/playground.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

/** @type {readonly ['compact', 'full']} */
export const PLAYGROUND_MODES = Object.freeze(['compact', 'full']);

/** @type {readonly ['html', 'react', 'vue', 'angular', 'svelte']} */
export const PLAYGROUND_CODE_FRAMEWORKS = Object.freeze(['html', 'react', 'vue', 'angular', 'svelte']);

/** @type {readonly ['loading', 'error', 'ready', 'dirty', 'sharing']} */
export const PLAYGROUND_INTERNAL_STATES = Object.freeze(['loading', 'error', 'ready', 'dirty', 'sharing']);

// Hard cap on retained event-log entries; oldest entries are dropped first.
const EVENT_LOG_LIMIT = 50;

class RunPlayground extends RunElement {
  // ── Private state ─────────────────────────────────────────────────────────
  #activePane = 'controls';
  #copied = false;
  #reducedMotion = false;
  #rendering = false;
  #copyTimeoutId = null;
  #sharingTimeoutId = null;
  #motionQuery = null;
  #tag = '';
  #contractData = null;
  #resolvedControls = [];
  #isDirty = false;
  #mode = 'compact';
  #showCode = true;
  // Tri-state: null = unset, so full mode can auto-enable the events panel.
  #showEventsExplicit = null;
  // Reserved panels (contract: accepted, descoped — no render output).
  #showA11y = false;
  #showPerformance = false;
  #showTokenCompliance = false;
  #showCssProps = false;
  #showSlots = false;
  #codeFrameworks = ['html'];
  #shareableUrl = true;
  #visibleProps = null;
  #initialProps = null;
  #compoundState = 'ready';
  #errorMessage = null;
  #eventLog = [];
  #activeAuxTab = null;
  #urlSeed = null;

  // ── Observed attributes ───────────────────────────────────────────────────
  static get observedAttributes() {
    return [
      'active-pane', 'copied', 'tag', 'mode', 'show-code', 'show-events',
      'show-a11y', 'show-performance', 'show-token-compliance',
      'show-css-props', 'show-slots', 'shareable-url',
    ];
  }

  // ── Arrow-function closures ───────────────────────────────────────────────
  // Private methods are NOT writable — never rebind with .bind(this).
  // Each handler is an arrow-function field that closes over `this`.
  _onTabsChangeBound       = (e) => this.#handleTabsChange(e);
  _onIconButtonClickBound  = (e) => this.#handleIconButtonClick(e);
  _onMotionChangeBound     = (e) => this.#handleMotionChange(e);
  _onControlChangeBound    = (e) => this.#handleControlChange(e);
  _onShadowClickBound      = (e) => this.#handleShadowClick(e);
  _onShadowKeydownBound    = (e) => this.#handleShadowKeydown(e);
  _onPreviewEventBound     = (e) => this.#handlePreviewEvent(e);

  constructor() {
    super();
  }

  // ── connectedCallback ─────────────────────────────────────────────────────
  connectedCallback() {
    super.connectedCallback();

    // Host role: landmark region wrapping the entire playground surface.
    this.setAttribute('role', 'region');
    this.#updateAriaLabel();

    // Reduced-motion preference (motion capability enabled).
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion = this.#motionQuery.matches;
    this.#motionQuery.addEventListener('change', this._onMotionChangeBound);

    // Delegated child events arrive on the shadow root because skin-rendered
    // chrome (icon buttons, controls, aux tabs) lives inside the shadow DOM.
    this.shadowRoot.addEventListener('run-tabs-change',        this._onTabsChangeBound);
    this.shadowRoot.addEventListener('run-icon-button-click',  this._onIconButtonClickBound);
    this.shadowRoot.addEventListener('run-change',             this._onControlChangeBound);
    this.shadowRoot.addEventListener('run-input',              this._onControlChangeBound);
    // run-button emits `run-click`; route all data-action buttons through the
    // same handler (reset, share, clear-events).
    this.shadowRoot.addEventListener('run-click',              this._onIconButtonClickBound);
    // Auxiliary tab buttons are plain skin-rendered <button role="tab">.
    this.shadowRoot.addEventListener('click',                  this._onShadowClickBound);
    this.shadowRoot.addEventListener('keydown',                this._onShadowKeydownBound);

    // Shareable-URL decode happens once, at connect; the seed is applied when
    // contractData derives the controls (the data may arrive after connect).
    if (this.#shareableUrl && this.#urlSeed === null) {
      this.#urlSeed = this.#decodeUrlSeed();
      if (this.#urlSeed) this.#applySeeds();
    }

    this.#updateAria();
    this.requestUpdate();
  }

  // ── disconnectedCallback ──────────────────────────────────────────────────
  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#motionQuery) {
      this.#motionQuery.removeEventListener('change', this._onMotionChangeBound);
      this.#motionQuery = null;
    }

    this.shadowRoot.removeEventListener('run-tabs-change',        this._onTabsChangeBound);
    this.shadowRoot.removeEventListener('run-icon-button-click',  this._onIconButtonClickBound);
    this.shadowRoot.removeEventListener('run-click',              this._onIconButtonClickBound);
    this.shadowRoot.removeEventListener('run-change',             this._onControlChangeBound);
    this.shadowRoot.removeEventListener('run-input',              this._onControlChangeBound);
    this.shadowRoot.removeEventListener('click',                  this._onShadowClickBound);
    this.shadowRoot.removeEventListener('keydown',                this._onShadowKeydownBound);

    if (this.#copyTimeoutId !== null) {
      clearTimeout(this.#copyTimeoutId);
      this.#copyTimeoutId = null;
    }
    if (this.#sharingTimeoutId !== null) {
      clearTimeout(this.#sharingTimeoutId);
      this.#sharingTimeoutId = null;
    }
  }

  // ── attributeChangedCallback ──────────────────────────────────────────────
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'active-pane': this.activePane = newValue ?? 'controls'; break;
      case 'copied':      this.copied     = newValue !== null;      break;
      case 'tag':         this.tag        = newValue ?? '';         break;
      case 'mode':        this.mode       = newValue ?? 'compact';  break;
      case 'show-code':   this.showCode   = this.#parseDefaultTrue(newValue); break;
      case 'show-events':
        this.#showEventsExplicit = newValue === null ? null : newValue !== 'false';
        this.requestUpdate();
        break;
      case 'show-a11y':             this.#showA11y            = newValue !== null && newValue !== 'false'; break;
      case 'show-performance':      this.#showPerformance     = newValue !== null && newValue !== 'false'; break;
      case 'show-token-compliance': this.#showTokenCompliance = newValue !== null && newValue !== 'false'; break;
      case 'show-css-props':        this.#showCssProps        = newValue !== null && newValue !== 'false'; break;
      case 'show-slots':            this.#showSlots           = newValue !== null && newValue !== 'false'; break;
      case 'shareable-url':         this.#shareableUrl        = this.#parseDefaultTrue(newValue); break;
    }
  }

  // Default-true boolean attribute parsing: absent → true; "false" → false;
  // any other value (including empty string) → true.
  #parseDefaultTrue(value) {
    if (value === null) return true;
    return value !== 'false';
  }

  // ── Public property: activePane (legacy pane switcher, kept for back-compat)
  get activePane() { return this.#activePane; }
  set activePane(value) {
    const VALID = ['controls', 'code'];
    const coerced = VALID.includes(String(value).trim()) ? String(value).trim() : 'controls';
    if (this.#activePane === coerced) return;
    this.#activePane = coerced;
    this.setAttribute('active-pane', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  // ── Public property: copied ───────────────────────────────────────────────
  get copied() { return this.#copied; }
  set copied(value) {
    const coerced = Boolean(value);
    if (this.#copied === coerced) return;
    this.#copied = coerced;
    this.toggleAttribute('copied', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  // ── Public property: tag ──────────────────────────────────────────────────
  get tag() { return this.#tag; }
  set tag(value) {
    const coerced = String(value ?? '').trim().toLowerCase();
    if (this.#tag === coerced) return;
    this.#tag = coerced;
    if (coerced) this.setAttribute('tag', coerced);
    else this.removeAttribute('tag');
    // A tag change invalidates any previous instantiation error.
    if (this.#compoundState === 'error') {
      this.#compoundState = 'ready';
      this.#errorMessage = null;
    }
    this.#updateAriaLabel();
    this.requestUpdate();
  }

  // ── Public property: mode ─────────────────────────────────────────────────
  get mode() { return this.#mode; }
  set mode(value) {
    const coerced = PLAYGROUND_MODES.includes(String(value).trim()) ? String(value).trim() : 'compact';
    if (this.#mode === coerced) return;
    this.#mode = coerced;
    this.setAttribute('mode', coerced);
    this.#activeAuxTab = null;
    this.requestUpdate();
  }

  // ── Public property: showCode ─────────────────────────────────────────────
  get showCode() { return this.#showCode; }
  set showCode(value) {
    const coerced = Boolean(value);
    if (this.#showCode === coerced) return;
    this.#showCode = coerced;
    if (coerced) this.removeAttribute('show-code');
    else this.setAttribute('show-code', 'false');
    this.#activeAuxTab = null;
    this.requestUpdate();
  }

  // ── Public property: showEvents ───────────────────────────────────────────
  // Default false in compact mode, auto-true in full mode; an explicit
  // assignment (attribute or property) wins in both modes.
  get showEvents() { return this.#showEventsExplicit ?? (this.#mode === 'full'); }
  set showEvents(value) {
    const coerced = Boolean(value);
    if (this.#showEventsExplicit === coerced) return;
    this.#showEventsExplicit = coerced;
    if (coerced) this.setAttribute('show-events', '');
    else this.setAttribute('show-events', 'false');
    this.#activeAuxTab = null;
    this.requestUpdate();
  }

  // ── Reserved panel flags (accepted, descoped — see contract.md) ───────────
  get showA11y() { return this.#showA11y; }
  set showA11y(value) { this.#showA11y = Boolean(value); this.toggleAttribute('show-a11y', this.#showA11y); }
  get showPerformance() { return this.#showPerformance; }
  set showPerformance(value) { this.#showPerformance = Boolean(value); this.toggleAttribute('show-performance', this.#showPerformance); }
  get showTokenCompliance() { return this.#showTokenCompliance; }
  set showTokenCompliance(value) { this.#showTokenCompliance = Boolean(value); this.toggleAttribute('show-token-compliance', this.#showTokenCompliance); }
  get showCssProps() { return this.#showCssProps; }
  set showCssProps(value) { this.#showCssProps = Boolean(value); this.toggleAttribute('show-css-props', this.#showCssProps); }
  get showSlots() { return this.#showSlots; }
  set showSlots(value) { this.#showSlots = Boolean(value); this.toggleAttribute('show-slots', this.#showSlots); }

  // ── Public property: codeFrameworks (property-only) ──────────────────────
  get codeFrameworks() { return this.#codeFrameworks; }
  set codeFrameworks(value) {
    const incoming = Array.isArray(value) ? value : [];
    const filtered = incoming.filter(f => PLAYGROUND_CODE_FRAMEWORKS.includes(f));
    this.#codeFrameworks = filtered.length > 0 ? filtered : ['html'];
    this.requestUpdate();
  }

  // ── Public property: shareableUrl ─────────────────────────────────────────
  get shareableUrl() { return this.#shareableUrl; }
  set shareableUrl(value) {
    const coerced = Boolean(value);
    if (this.#shareableUrl === coerced) return;
    this.#shareableUrl = coerced;
    if (coerced) this.removeAttribute('shareable-url');
    else this.setAttribute('shareable-url', 'false');
  }

  // ── Public property: visibleProps (property-only) ─────────────────────────
  get visibleProps() { return this.#visibleProps; }
  set visibleProps(value) {
    this.#visibleProps = Array.isArray(value) ? value.map(String) : null;
    this.#resolvedControls = this.#deriveControls(this.#contractData);
    this.#applySeeds();
    this.requestUpdate();
  }

  // ── Public property: initialProps (property-only) ─────────────────────────
  get initialProps() { return this.#initialProps; }
  set initialProps(value) {
    this.#initialProps = (value && typeof value === 'object') ? value : null;
    this.#applySeeds();
    this.requestUpdate();
  }

  // ── Public property: contractData ─────────────────────────────────────────
  // Contract manifest fragment describing the target component. Drives
  // _resolvedControls (the skin's control panel) and the preview snippet.
  // Recognised fields: properties[] ({name, attribute, type, default,
  // description}), events[] ({name}), status, previewHtml (initial preview
  // markup; falls back to a programmatically created <tag> element).
  get contractData() { return this.#contractData; }
  set contractData(value) {
    const coerced = (value && typeof value === 'object') ? value : null;
    if (this.#contractData === coerced) return;
    this.#contractData = coerced;
    this.#resolvedControls = this.#deriveControls(coerced);
    this.#isDirty = false;
    this.#applySeeds();
    this.requestUpdate();
  }

  // ── Read-only host surface consumed by the skin ───────────────────────────
  get _compoundState() { return this.#compoundState; }
  get _errorMessage() { return this.#errorMessage; }
  get _resolvedControls() { return this.#resolvedControls; }
  get _isDirty() { return this.#isDirty; }
  get _eventLog() { return this.#eventLog; }
  get _generatedCode() { return { html: this.#snippet() }; }
  get _activeAuxTab() { return this.#activeAuxTab; }

  // ── Public methods (contract) ─────────────────────────────────────────────

  /** Returns a shallow snapshot of the current control values keyed by name. */
  getState() {
    const out = {};
    for (const ctrl of this.#resolvedControls) out[ctrl.name] = ctrl.currentValue;
    return out;
  }

  /** Merges props into the controls, updates the target, regenerates code. */
  setState(props) {
    if (!props || typeof props !== 'object') return;
    let touched = false;
    for (const ctrl of this.#resolvedControls) {
      if (!(ctrl.name in props)) continue;
      ctrl.currentValue = ctrl.controlType === 'checkbox'
        ? props[ctrl.name] === true || props[ctrl.name] === 'true'
        : props[ctrl.name];
      touched = true;
    }
    if (!touched) return;
    this.#safeApplyControlValues();
    this.#refreshCodeDisplays();
    this.#markDirty();
    this.requestUpdate();
  }

  /** Resets controls to contract defaults; emits run-playground-reset. */
  reset() {
    this.#triggerReset();
  }

  /** Delegates focus to the first focusable control in the controls panel. */
  focus(options) {
    const first = this.shadowRoot?.querySelector('[part="controls"] [data-prop]');
    if (first?.focus) first.focus(options);
    else super.focus?.(options);
  }

  // ── Private: control derivation ───────────────────────────────────────────
  // Maps contract properties to skin control descriptors:
  //   union-of-literals type ("'a' | 'b' | 'c'") -> select with those options
  //   boolean                                    -> checkbox
  //   number                                     -> number input
  //   anything else                              -> text input
  // The 'skin' property is skin-cascade plumbing, not a playground knob.
  #deriveControls(contractData) {
    const props = Array.isArray(contractData?.properties) ? contractData.properties : [];
    return props
      .filter(p => p.attribute && p.name !== 'skin')
      .filter(p => this.#visibleProps === null || this.#visibleProps.includes(p.name))
      .map(p => {
        const type = String(p.type ?? '');
        const literals = [...type.matchAll(/'([^']*)'/g)].map(m => m[1]).filter(v => v !== '');
        const dflt = (() => {
          const d = String(p.default ?? '').replace(/^'|'$/g, '');
          return d === 'null' || d === 'undefined' ? '' : d;
        })();
        const base = {
          name: p.name,
          attribute: p.attribute,
          description: p.description ?? '',
          defaultValue: dflt,
          hasError: false,
        };
        if (type.includes('boolean')) {
          return { ...base, controlType: 'checkbox', currentValue: dflt === 'true' };
        }
        if (literals.length >= 2) {
          return { ...base, controlType: 'select', options: literals, currentValue: dflt };
        }
        if (type.includes('number')) {
          return { ...base, controlType: 'input', inputType: 'number', currentValue: dflt };
        }
        return { ...base, controlType: 'input', inputType: 'text', currentValue: dflt };
      });
  }

  // ── Private: seed application (initialProps + decoded URL state) ──────────
  // URL state wins over initialProps; both win over contract defaults.
  #applySeeds() {
    if (this.#resolvedControls.length === 0) return;
    const urlProps = (this.#urlSeed && this.#urlSeed.tag === this.#tag) ? this.#urlSeed.props : null;
    const seed = { ...(this.#initialProps ?? {}), ...(urlProps ?? {}) };
    if (Object.keys(seed).length === 0) return;
    for (const ctrl of this.#resolvedControls) {
      if (!(ctrl.name in seed)) continue;
      ctrl.currentValue = ctrl.controlType === 'checkbox'
        ? seed[ctrl.name] === true || seed[ctrl.name] === 'true'
        : seed[ctrl.name];
    }
  }

  #decodeUrlSeed() {
    try {
      // Derive from href, not location.search — href is the one field every
      // environment (including the test harness's patched location) carries.
      const raw = new URL(window.location.href).searchParams.get('pg');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || typeof parsed.tag !== 'string') return null;
      return { tag: parsed.tag, props: (parsed.props && typeof parsed.props === 'object') ? parsed.props : {} };
    } catch {
      return null;
    }
  }

  #encodeShareUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('pg', JSON.stringify({ tag: this.#tag, props: this.getState() }));
    return url.toString();
  }

  // ── Private: preview mounting + value application ─────────────────────────
  #previewMountKey() {
    return this.#contractData?.previewHtml ?? (this.#tag ? `tag:${this.#tag}` : '');
  }

  #mountPreview() {
    const hostEl = this.shadowRoot?.querySelector('.preview-host');
    if (!hostEl) return;
    const key = this.#previewMountKey();
    if (!key) return;
    if (hostEl.dataset.pgMounted !== key) {
      try {
        if (this.#contractData?.previewHtml) {
          // Manifest-supplied preview markup (trusted manifest content) so
          // slotted example content (e.g. a button label) is real.
          hostEl.innerHTML = this.#contractData.previewHtml;
        } else {
          hostEl.textContent = '';
          const created = document.createElement(this.#tag);
          created.textContent = this.#tag;
          hostEl.appendChild(created);
        }
        hostEl.dataset.pgMounted = key;
        if (this.#compoundState === 'error') {
          this.#setCompoundState('ready');
          this.#errorMessage = null;
        }
      } catch (error) {
        this.#containError(error, 'target-instantiation');
        return;
      }
    }
    this.#safeApplyControlValues();
    this.#attachPreviewEventListeners(hostEl);
  }

  #previewTarget() {
    const hostEl = this.shadowRoot?.querySelector('.preview-host');
    if (!hostEl) return null;
    return (this.#tag && hostEl.querySelector(this.#tag)) || hostEl.firstElementChild;
  }

  #safeApplyControlValues() {
    try {
      this.#applyControlValues();
    } catch (error) {
      this.#containError(error, 'prop-application');
    }
  }

  #applyControlValues() {
    const target = this.#previewTarget();
    if (!target) return;
    for (const ctrl of this.#resolvedControls) {
      const attr = ctrl.attribute;
      if (ctrl.controlType === 'checkbox') {
        target.toggleAttribute(attr, ctrl.currentValue === true);
      } else if (String(ctrl.currentValue ?? '') === '' || String(ctrl.currentValue) === ctrl.defaultValue) {
        // Defaults stay off the element so the preview reflects real usage.
        if (String(ctrl.currentValue) === ctrl.defaultValue && ctrl.defaultValue !== '') {
          target.setAttribute(attr, ctrl.currentValue);
        } else {
          target.removeAttribute(attr);
        }
      } else {
        target.setAttribute(attr, ctrl.currentValue);
      }
    }
  }

  #snippet() {
    const target = this.#previewTarget();
    const tag = this.#tag || 'div';
    const inner = target ? target.innerHTML.trim() : '';
    const attrs = this.#resolvedControls
      .map(ctrl => {
        if (ctrl.controlType === 'checkbox') {
          return ctrl.currentValue === true ? ` ${ctrl.attribute}` : '';
        }
        const v = String(ctrl.currentValue ?? '');
        return v !== '' && v !== ctrl.defaultValue ? ` ${ctrl.attribute}="${v}"` : '';
      })
      .join('');
    return `<${tag}${attrs}>${inner}</${tag}>`;
  }

  // ── Private: event interception (contract: run-playground-event) ──────────
  // Listeners attach to the preview host in the capture phase, so events from
  // the live target are seen regardless of their bubbles flag. Event names
  // come from contractData.events — the manifest declares what the target
  // can emit; without a declaration there is nothing to intercept.
  #attachPreviewEventListeners(hostEl) {
    const names = (Array.isArray(this.#contractData?.events) ? this.#contractData.events : [])
      .map(e => e?.name)
      .filter(n => typeof n === 'string' && n.length > 0);
    const key = names.join(',');
    if (hostEl.dataset.pgEvents === key) return;
    // The previous host element (if any) was discarded with the last skin
    // render, so stale listeners die with it; only attach to the current one.
    for (const name of names) {
      hostEl.addEventListener(name, this._onPreviewEventBound, true);
    }
    hostEl.dataset.pgEvents = key;
  }

  #handlePreviewEvent(event) {
    // Ignore events from playground chrome that bubbled into the preview
    // region; only log events originating at or under the live target.
    const target = this.#previewTarget();
    if (!target) return;
    const origin = event.composedPath?.()[0] ?? event.target;
    if (origin !== target && !target.contains(origin)) return;

    const entry = {
      eventName: event.type,
      detail: event.detail ?? null,
      timestamp: Date.now(),
    };
    this.#eventLog.push(entry);
    if (this.#eventLog.length > EVENT_LOG_LIMIT) this.#eventLog.shift();

    this.dispatchEvent(new CustomEvent('run-playground-event', {
      bubbles: false,
      composed: false,
      detail: { ...entry },
    }));

    this.#refreshEventsPanel();
  }

  #clearEventLog() {
    this.#eventLog = [];
    this.#refreshEventsPanel();
  }

  // Imperative refresh of the events list — a full skin re-render would
  // destroy focus inside the preview or controls while the user interacts.
  #refreshEventsPanel() {
    const list = this.shadowRoot?.querySelector('[data-event-log]');
    if (!list) return;
    list.innerHTML = this.#eventLog.length === 0
      ? `<span class="event-log-empty">No events captured yet.</span>`
      : `<ul class="event-log-list">${this.#eventLog.map(ev =>
          `<li class="event-log-item"><strong>${ev.eventName}</strong> — ${new Date(ev.timestamp).toLocaleTimeString()}</li>`
        ).join('')}</ul>`;
  }

  // Imperative refresh of every code display — same focus-preservation
  // rationale as the events panel.
  #refreshCodeDisplays() {
    const code = this.#snippet();
    this.shadowRoot?.querySelectorAll('run-code-display').forEach(cd => {
      cd.code = code;
    });
  }

  // ── Private: control change handler ───────────────────────────────────────
  #handleControlChange(event) {
    const source = event.composedPath?.()[0] ?? event.target;
    const controlEl = source?.closest?.('[data-prop]') ?? (source?.dataset?.prop ? source : null);
    const propName = controlEl?.dataset?.prop;
    if (!propName) return;
    event.stopPropagation();
    const ctrl = this.#resolvedControls.find(c => c.name === propName);
    if (!ctrl) return;
    if (ctrl.controlType === 'checkbox') {
      ctrl.currentValue = event.detail?.checked ?? controlEl.hasAttribute('checked');
    } else {
      ctrl.currentValue = event.detail?.value ?? controlEl.value ?? '';
    }
    this.#safeApplyControlValues();
    this.#refreshCodeDisplays();
    this.dispatchEvent(new CustomEvent('run-playground-change', {
      bubbles: false,
      composed: false,
      detail: { props: this.getState(), tag: this.#tag },
    }));
    this.#markDirty();
  }

  // Dirty transition without a full re-render: enable the reset button
  // imperatively so the focused control keeps focus.
  #markDirty() {
    if (this.#isDirty) return;
    this.#isDirty = true;
    this.shadowRoot?.querySelector('[data-action="reset"]')?.removeAttribute('disabled');
    this.#setCompoundState('dirty');
  }

  // ── Skin wiring ───────────────────────────────────────────────────────────

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    // The skin renders an empty .preview-host; the headless owns the live
    // preview instance (contract: target instantiated programmatically).
    this.#mountPreview();
    this.#refreshCodeDisplays();
    this.#refreshEventsPanel();
    this.#updateAria();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  // ── Private: ARIA synchronisation ─────────────────────────────────────────
  #updateAriaLabel() {
    this.setAttribute('aria-label', this.#tag ? `Playground for ${this.#tag}` : 'Component playground');
  }

  #updateAria() {
    // data-active-pane — legacy pane indicator (kept for back-compat).
    this.setAttribute('data-active-pane', this.#activePane);

    // data-copied — read by skin to toggle the copy-success indicator on the
    // icon-button rendered by the skin.
    if (this.#copied) {
      this.setAttribute('data-copied', '');
    } else {
      this.removeAttribute('data-copied');
    }

    // data-reduced-motion — read by skin to suppress CSS transitions.
    if (this.#reducedMotion) {
      this.setAttribute('data-reduced-motion', '');
    } else {
      this.removeAttribute('data-reduced-motion');
    }
  }

  // Announce a compound-state transition in the skin-rendered polite live
  // region (contract: Accessibility — live region).
  #setCompoundState(next, announceText) {
    if (!PLAYGROUND_INTERNAL_STATES.includes(next)) return;
    this.#compoundState = next;
    const region = this.shadowRoot?.querySelector('[data-announce-polite]');
    if (region) {
      region.textContent = announceText ?? `Playground ${next}`;
    }
  }

  // ── Private: error containment ────────────────────────────────────────────
  #containError(error, context) {
    this.#errorMessage = error?.message ?? String(error);
    this.#setCompoundState('error');
    this.dispatchEvent(new CustomEvent('run-playground-error', {
      bubbles: false,
      composed: false,
      detail: { error, context },
    }));
    this.requestUpdate();
  }

  // ── Private: delegated child event handlers ───────────────────────────────

  #handleMotionChange(event) {
    this.#reducedMotion = event.matches;
    this.#updateAria();
    this.requestUpdate();
  }

  #handleTabsChange(event) {
    event.stopPropagation();
    // run-tabs emits { value } or { tab } on its change event.
    const incoming = event.detail?.value ?? event.detail?.tab ?? 'controls';
    const previous = this.#activePane;
    this.activePane = incoming;
    if (this.#activePane !== previous) {
      this.#emitPaneChange(event.target);
    }
  }

  #handleIconButtonClick(event) {
    // The skin stamps a data-action attribute on each action element so the
    // compound can distinguish actions without coupling to skin internals.
    const source = event.composedPath?.()[0] ?? event.target;
    const actionEl = source?.closest?.('[data-action]');
    const action = actionEl?.dataset?.action ?? event.detail?.action ?? '';
    if (!action) return;
    event.stopPropagation();
    switch (action) {
      case 'share':
      case 'copy':         this.#triggerShare();      break;
      case 'reset':        this.#triggerReset(true);  break;
      case 'clear-events': this.#clearEventLog();     break;
    }
  }

  // Auxiliary tab buttons are skin-rendered plain <button role="tab">.
  #handleShadowClick(event) {
    const btn = (event.composedPath?.()[0] ?? event.target)?.closest?.('.tab-btn');
    if (!btn || !btn.dataset.tab) return;
    this.#setActiveAuxTab(btn.dataset.tab);
  }

  #handleShadowKeydown(event) {
    if (event.key === 'Escape' && this.#compoundState === 'sharing') {
      this.#setCompoundState('ready');
      return;
    }
    // Roving arrow-key navigation across the auxiliary tablist.
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const current = (event.composedPath?.()[0] ?? event.target)?.closest?.('.tab-btn');
    if (!current) return;
    const tabs = [...this.shadowRoot.querySelectorAll('.tab-btn')];
    const idx = tabs.indexOf(current);
    if (idx === -1) return;
    event.preventDefault();
    const next = tabs[(idx + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    this.#setActiveAuxTab(next.dataset.tab);
    next.focus();
  }

  // Imperative tab activation — keeps panel content (e.g. a populated event
  // log) intact instead of rebuilding the shadow tree.
  #setActiveAuxTab(key) {
    if (!key || this.#activeAuxTab === key) return;
    this.#activeAuxTab = key;
    this.shadowRoot.querySelectorAll('.tab-btn').forEach(btn => {
      const selected = btn.dataset.tab === key;
      btn.setAttribute('aria-selected', selected ? 'true' : 'false');
      btn.setAttribute('tabindex', selected ? '0' : '-1');
    });
    this.shadowRoot.querySelectorAll('.tab-panel').forEach(panel => {
      panel.toggleAttribute('hidden', panel.id !== `tabpanel-${key}`);
    });
  }

  // ── Private: event emitters ───────────────────────────────────────────────

  #emitPaneChange(sourceEl) {
    this.dispatchEvent(new CustomEvent('run-playground-pane-change', {
      bubbles: true,
      composed: true,
      detail: { pane: this.#activePane, source: sourceEl?.localName ?? null },
    }));
  }

  #emitShare(url) {
    this.dispatchEvent(new CustomEvent('run-playground-share', {
      bubbles: false,
      composed: false,
      detail: { url, props: this.getState() },
    }));
    // Legacy event kept for pre-contract consumers.
    this.dispatchEvent(new CustomEvent('run-playground-copy', {
      bubbles: true,
      composed: true,
      detail: { code: url },
    }));
  }

  #emitReset() {
    this.dispatchEvent(new CustomEvent('run-playground-reset', {
      bubbles: true,
      composed: true,
      detail: { tag: this.#tag },
    }));
  }

  // ── Private: state transitions ────────────────────────────────────────────

  #triggerShare() {
    const url = this.#shareableUrl ? this.#encodeShareUrl() : '';
    this.#setCompoundState('sharing', 'Shareable link ready');
    if (this.#sharingTimeoutId !== null) clearTimeout(this.#sharingTimeoutId);
    this.#sharingTimeoutId = setTimeout(() => {
      if (this.#compoundState === 'sharing') this.#setCompoundState(this.#isDirty ? 'dirty' : 'ready');
      this.#sharingTimeoutId = null;
    }, 2000);

    if (navigator.clipboard && url) {
      navigator.clipboard.writeText(url).then(() => {
        this.#setCopied(true);
        this.#emitShare(url);
        // Contract: focus returns to the triggering action after share.
        this.updateComplete.then(() => {
          this.shadowRoot?.querySelector('[data-action="share"]')?.focus?.();
        });
      }).catch(() => {
        this.#emitShare(url);
      });
    } else {
      // Emit even when clipboard is unavailable so consumers can fall back.
      this.#emitShare(url);
    }
  }

  #setCopied(value) {
    this.copied = value;
    if (value) {
      if (this.#copyTimeoutId !== null) clearTimeout(this.#copyTimeoutId);
      this.#copyTimeoutId = setTimeout(() => {
        this.copied = false;
        this.#copyTimeoutId = null;
      }, 2000);
    }
  }

  #triggerReset(fromUi = false) {
    // Reset playground-owned state.
    this.activePane = 'controls';
    this.copied = false;
    // Restore every control (and thus the preview) to contract defaults.
    for (const ctrl of this.#resolvedControls) {
      ctrl.currentValue = ctrl.controlType === 'checkbox'
        ? ctrl.defaultValue === 'true'
        : ctrl.defaultValue;
    }
    this.#isDirty = false;
    this.#safeApplyControlValues();
    this.#refreshCodeDisplays();
    this.#setCompoundState('ready', 'Controls reset to defaults');
    this.requestUpdate();
    if (fromUi) {
      // Contract: focus returns to the triggering action after reset.
      this.updateComplete.then(() => {
        this.shadowRoot?.querySelector('[data-action="reset"]')?.focus?.();
      });
    }
    this.#emitReset();
  }
}

if (!customElements.get('run-playground')) {
  customElements.define('run-playground', RunPlayground);
}

export { RunPlayground };
export default RunPlayground;
